// src/features/automacaodeemails/pages/AssetsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";

const TYPE_OPTIONS = [
  { value: "signature", label: "Assinatura" },
  { value: "image", label: "Imagem" },
];

export default function AssetsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assets, setAssets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({
    tipo: "signature",
    nome: "",
    descricao: "",
    img_base64: "",
    img_mime: "",
    img_nome: "",
    ativo: true,
  });
  const [uploadPreview, setUploadPreview] = useState("");

  useEffect(() => {
    loadAssets();
  }, []);

  async function loadAssets() {
    setLoading(true);
    try {
      const res = await api.assets.list();
      setAssets(res?.data || res || []);
    } catch (err) {
      console.error("Erro ao carregar imagens:", err);
    } finally {
      setLoading(false);
    }
  }

  function handleFile(file) {
    if (!file) return;
    if (file.size > 1024 * 1024) {
      alert("A imagem deve ter ate 1 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || "");
      const match = raw.match(/^data:(.*?);base64,(.*)$/);
      if (!match) return;
      setForm((prev) => ({
        ...prev,
        img_mime: match[1],
        img_base64: match[2],
        img_nome: file.name || "imagem.png",
      }));
      setUploadPreview(raw);
    };
    reader.readAsDataURL(file);
  }

  async function handleSave() {
    if (!form.img_base64) {
      alert("Selecione uma imagem.");
      return;
    }
    setSaving(true);
    try {
      await api.assets.create(form);
      setForm({
        tipo: "signature",
        nome: "",
        descricao: "",
        img_base64: "",
        img_mime: "",
        img_nome: "",
        ativo: true,
      });
      setUploadPreview("");
      await loadAssets();
    } catch (err) {
      alert(`Erro ao salvar: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(asset) {
    if (!window.confirm(`Excluir "${asset.nome || asset.img_nome || "imagem"}"?`)) return;
    try {
      await api.assets.delete(asset.id);
      await loadAssets();
    } catch (err) {
      alert(`Erro ao excluir: ${err?.message}`);
    }
  }

  const filteredAssets = useMemo(() => {
    if (filter === "all") return assets;
    return assets.filter((a) => a.tipo === filter);
  }, [assets, filter]);

  return (
    <div className="ae2-page">
      <div className="ae2-page-header">
        <div>
          <h2>Assinaturas e imagens</h2>
          <p>Salve imagens no banco para reutilizar nas automacoes.</p>
        </div>
      </div>

      <div className="ae2-form-grid">
        <div className="ae2-card">
          <h3>Adicionar imagem</h3>
          <div className="ae2-field">
            <label>Tipo</label>
            <select
              value={form.tipo}
              onChange={(e) => setForm((prev) => ({ ...prev, tipo: e.target.value }))}
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="ae2-field">
            <label>Nome</label>
            <input
              type="text"
              value={form.nome}
              onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
              placeholder="Ex: Assinatura Padrao"
            />
          </div>
          <div className="ae2-field">
            <label>Descricao (opcional)</label>
            <textarea
              rows={3}
              value={form.descricao}
              onChange={(e) => setForm((prev) => ({ ...prev, descricao: e.target.value }))}
              placeholder="Use para explicar quando usar a imagem."
            />
          </div>
          <div className="ae2-upload">
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            <span className="ae2-muted">PNG/JPG ate 1 MB</span>
          </div>
          {uploadPreview && (
            <div className="ae2-signature-preview">
              <img src={uploadPreview} alt="Preview" />
              <button
                type="button"
                className="ae2-btn ae2-btn-ghost"
                onClick={() => {
                  setForm((prev) => ({
                    ...prev,
                    img_base64: "",
                    img_mime: "",
                    img_nome: "",
                  }));
                  setUploadPreview("");
                }}
              >
                Remover imagem
              </button>
            </div>
          )}
          <button className="ae2-btn ae2-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Salvando..." : "Salvar imagem"}
          </button>
        </div>

        <div className="ae2-card">
          <div className="ae2-card-header">
            <h3>Galeria</h3>
            <div className="ae2-inline-actions">
              <select
                className="ae2-filter-select"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">Todos</option>
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="ae2-muted">Carregando...</div>
          ) : filteredAssets.length === 0 ? (
            <div className="ae2-empty">
              <p>Nenhuma imagem cadastrada.</p>
            </div>
          ) : (
            <div className="ae2-assets-grid">
              {filteredAssets.map((asset) => (
                <div key={asset.id} className="ae2-asset-card">
                  <div className="ae2-asset-preview">
                    {asset.img_base64 && asset.img_mime ? (
                      <img
                        src={`data:${asset.img_mime};base64,${asset.img_base64}`}
                        alt={asset.nome || asset.img_nome || "Imagem"}
                      />
                    ) : (
                      <div className="ae2-muted">Sem imagem</div>
                    )}
                  </div>
                  <div className="ae2-asset-info">
                    <div className="ae2-asset-title">{asset.nome || asset.img_nome || "Imagem"}</div>
                    <div className="ae2-asset-meta">
                      <span className="ae2-badge info">{asset.tipo === "signature" ? "Assinatura" : "Imagem"}</span>
                      {asset.descricao && <span>{asset.descricao}</span>}
                    </div>
                  </div>
                  <div className="ae2-inline-actions">
                    <button className="ae2-btn ae2-btn-danger" onClick={() => handleDelete(asset)}>
                      Excluir
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
